import { addDays, addYears, isAfter, max, startOfDay, startOfYear } from 'date-fns';
import { serviceModel, serviceCostModel, makeAssignedUserUnassigned, makeAssignedUserAssigned } from "../../models";
import { DiscountPeriodExpirationModel } from "../../models/DiscountPeriodExpirationModel";
import { DiscountExpirationType, LimitedTimeDiscountOfferModel } from "../../models/LimitedTimeDiscountOfferModel";
import { fromDateStampObject } from "../../models/time";
import { AdvancedSetting, IncreaseMethod } from "./ServiceAdvancedSettings.types";
import { StartAutomaticBillingAtControlDropdownMenuItem } from "./StartAutomaticBillingAtControl/StartAutomaticBillingAtControl";
export function getStartAutomaticBillingAtSelection(service) {
  if (serviceModel.isPaused(service)) {
    return StartAutomaticBillingAtControlDropdownMenuItem.Pause;
  }
  if (serviceModel.startBillingAt(service)) {
    return StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate;
  }
  if (serviceModel.isBillOnPeriodBeforeNextBilling(service)) {
    return StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate;
  }
  return null;
}
const isSettingActiveForServiceIntegration = (service, getServiceIntegration) => {
  const isSettingActiveForService = service => {
    const integration = getServiceIntegration(service);
    return !!integration && (integration.isEnabled() || integration.hasAssignedTeamMembers());
  };
  if (service.bundle) {
    return service.bundle.services.some(isSettingActiveForService);
  }
  return isSettingActiveForService(service);
};
export const isSettingActive = _ref => {
  let {
    type,
    service
  } = _ref;
  switch (type) {
    case AdvancedSetting.Pause:
      return serviceModel.isPaused(service);
    case AdvancedSetting.BillOnApproval:
      return !serviceModel.isSkipBillingOnAcceptance(service);
    case AdvancedSetting.Prorate:
      return serviceModel.isProrated(service);
    case AdvancedSetting.Optional:
      return service.participation.type === 'optional';
    case AdvancedSetting.LimitCharges:
      return serviceModel.maxChargesEnabled(service);
    case AdvancedSetting.AutoPriceIncrease:
      {
        var _serviceChanges$cost;
        if (serviceModel.priceIncrease(service).exists()) {
          return true;
        }
        const serviceChanges = serviceModel.changes(service);
        if (serviceChanges !== null && serviceChanges !== void 0 && (_serviceChanges$cost = serviceChanges.cost) !== null && _serviceChanges$cost !== void 0 && _serviceChanges$cost.priceIncrease) {
          return serviceChanges.cost.priceIncrease.exists();
        }
        return false;
      }
    case AdvancedSetting.QboClasses:
      return serviceModel.isIntegratedClassSet(service);
    case AdvancedSetting.FinancialCents:
      {
        return isSettingActiveForServiceIntegration(service, service => service.integrations.financialCentsIntegration());
      }
    case AdvancedSetting.Karbon:
      {
        return isSettingActiveForServiceIntegration(service, service => service.integrations.karbonIntegration());
      }
    case AdvancedSetting.LimitedTimeDiscountOffer:
      return !!serviceModel.limitedTimeDiscountOffer(service);
    case AdvancedSetting.LimitedTimeDiscountOfferForAgreement:
      {
        var _serviceModel$discoun, _serviceChanges$cost2;
        const discount = service.cost.discount;
        if (discount && (_serviceModel$discoun = serviceModel.discount(service)) !== null && _serviceModel$discoun !== void 0 && (_serviceModel$discoun = _serviceModel$discoun.expiration()) !== null && _serviceModel$discoun !== void 0 && _serviceModel$discoun.hasExpiration()) {
          return true;
        }
        const serviceChanges = serviceModel.changes(service);
        if (serviceChanges !== null && serviceChanges !== void 0 && (_serviceChanges$cost2 = serviceChanges.cost) !== null && _serviceChanges$cost2 !== void 0 && _serviceChanges$cost2.discount) {
          var _serviceModel$discoun2;
          return !!((_serviceModel$discoun2 = serviceModel.discount({
            cost: serviceChanges.cost
          })) !== null && _serviceModel$discoun2 !== void 0 && (_serviceModel$discoun2 = _serviceModel$discoun2.expiration()) !== null && _serviceModel$discoun2 !== void 0 && _serviceModel$discoun2.hasExpiration());
        }
        return false;
      }
    case AdvancedSetting.PreapprovedAmount:
      {
        var _serviceModel$changes;
        const cost = ((_serviceModel$changes = serviceModel.changes(service)) === null || _serviceModel$changes === void 0 ? void 0 : _serviceModel$changes.cost) || service.cost;
        if (serviceCostModel.preapprovedControlledByAdvancedSettings(cost)) {
          return true;
        }
        if (serviceModel.hasPreapprovedAmount(service)) {
          return true;
        }
        const serviceChanges = serviceModel.changes(service);
        if (serviceChanges !== null && serviceChanges !== void 0 && serviceChanges.cost) {
          return serviceModel.hasPreapprovedAmount({
            cost: serviceChanges.cost
          });
        }
        return false;
      }
    case AdvancedSetting.StartAutomaticBillingAt:
      return getStartAutomaticBillingAtSelection(service) !== null;
    default:
      return false;
  }
};
export function getAmendedMinDate(now, relationshipEffectiveDate) {
  const tomorrowDate = addDays(now, 1);
  return relationshipEffectiveDate ? max([tomorrowDate, relationshipEffectiveDate]) : tomorrowDate;
}
export function getInitialStartBillingAtDate(_ref2) {
  let {
    now = new Date(),
    relationshipEffectiveDate = null,
    isAmendedService = false
  } = _ref2;
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  if (isAmendedService) {
    return relationshipEffectiveDate ? max([tomorrow, relationshipEffectiveDate]) : tomorrow;
  }
  if (relationshipEffectiveDate) {
    return max([startOfDay(relationshipEffectiveDate), today]);
  }
  return tomorrow;
}
export function getMinimumEffectiveDate(_ref3) {
  let {
    now = new Date(),
    relationshipEffectiveDate = null,
    isAmendedService = false
  } = _ref3;
  const today = startOfDay(now);
  const tomorrow = addDays(today, 1);
  if (isAmendedService) {
    return relationshipEffectiveDate ? max([tomorrow, relationshipEffectiveDate]) : tomorrow;
  }
  return relationshipEffectiveDate ? addYears(today, -1) : tomorrow;
}
export function updateStartBillingAtByMenuItem(_ref4) {
  let {
    item,
    value,
    service,
    onPausedChange,
    onBillOnAcceptanceChange,
    onStartBillingAtChange
  } = _ref4;
  switch (item) {
    case StartAutomaticBillingAtControlDropdownMenuItem.Pause:
      onPausedChange === null || onPausedChange === void 0 || onPausedChange(service.id, Boolean(value));
      break;
    case StartAutomaticBillingAtControlDropdownMenuItem.AcceptanceDate:
      onBillOnAcceptanceChange === null || onBillOnAcceptanceChange === void 0 || onBillOnAcceptanceChange(service.id, Boolean(value));
      break;
    case StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate:
      onStartBillingAtChange === null || onStartBillingAtChange === void 0 || onStartBillingAtChange(service.id, value instanceof Date ? value : null);
      break;
  }
}

// price Increase Utils
export function getInitialIncreaseMethod(priceIncrease, relationshipState) {
  if (priceIncrease.exists()) {
    return priceIncrease.increaseOn() ? IncreaseMethod.Date : IncreaseMethod.EffectiveDate;
  } else {
    return relationshipState.type === 'awaitingApproval' && !relationshipState.effectiveDate ? IncreaseMethod.EffectiveDate : IncreaseMethod.Date;
  }
}
export function getValidIncreaseOn(increaseOn, now) {
  const today = startOfDay(now);
  return isAfter(increaseOn, today) ? increaseOn : addYears(increaseOn, 1);
}
export function defaultIncreaseOn(service, now) {
  const priceIncrease = serviceModel.priceIncrease(service);
  return priceIncrease.increaseOn() || startOfYear(addYears(now, 1));
}
export function defaultPrecentIncrease(service) {
  const priceIncrease = serviceModel.priceIncrease(service);
  if (!priceIncrease.exists() || priceIncrease.percentIncrease().toNumber() < 0) {
    return '';
  }
  return priceIncrease.percentIncrease().toString();
}
export function defaultRoundPrices(service) {
  return serviceModel.priceIncrease(service).roundPrices();
}
export function defaultIncreaseMethod(priceIncrease, relationshipState) {
  return getInitialIncreaseMethod(priceIncrease, relationshipState);
}

// Discount Period Expiration Utils
export function getInitialDiscountPeriodExpiration(_ref5) {
  let {
    now = new Date(),
    isApprovedRelationship = false
  } = _ref5;
  if (isApprovedRelationship) {
    const baseDate = startOfDay(now);
    return DiscountPeriodExpirationModel.makeDate(addYears(baseDate, 1));
  } else {
    return DiscountPeriodExpirationModel.makePeriod('years', 1);
  }
}
export function getInitialDiscountPeriodExpirationState(_ref6) {
  var _defaultExpiration$da, _defaultExpiration$pe, _defaultExpiration$am;
  let {
    now = new Date(),
    isApprovedRelationship = false,
    existingExpiration
  } = _ref6;
  if (existingExpiration && existingExpiration.type() !== 'none') {
    const date = existingExpiration.date();
    const period = existingExpiration.period();
    const amount = existingExpiration.amount();
    if (existingExpiration.isExpirationDateType() && date) {
      return {
        selectedDate: date,
        timeframeType: 'date',
        periodType: 'years',
        amount: 1
      };
    } else if (existingExpiration.isExpirationPeriodType() && period && amount !== undefined) {
      return {
        selectedDate: addYears(now, 1),
        timeframeType: 'period',
        periodType: period,
        amount
      };
    }
  }
  const defaultExpiration = getInitialDiscountPeriodExpiration({
    now,
    isApprovedRelationship
  });
  const defaultDate = (_defaultExpiration$da = defaultExpiration.date()) !== null && _defaultExpiration$da !== void 0 ? _defaultExpiration$da : addYears(now, 1);
  const defaultPeriod = (_defaultExpiration$pe = defaultExpiration.period()) !== null && _defaultExpiration$pe !== void 0 ? _defaultExpiration$pe : 'years';
  const defaultAmount = (_defaultExpiration$am = defaultExpiration.amount()) !== null && _defaultExpiration$am !== void 0 ? _defaultExpiration$am : 1;
  return {
    selectedDate: defaultDate,
    timeframeType: isApprovedRelationship ? 'date' : 'period',
    periodType: defaultPeriod,
    amount: defaultAmount
  };
}
export function getInitialLimitedTimeDiscountOffer() {
  const expiration = {
    type: DiscountExpirationType.Days,
    days: 30
  };
  return new LimitedTimeDiscountOfferModel(expiration);
}
export function getInitialLimitedTimeDiscountOfferState(_ref7) {
  let {
    now = new Date(),
    existingOffer
  } = _ref7;
  if (existingOffer) {
    const expirationType = existingOffer.DiscountExpirationType();
    const days = existingOffer.ExpirationDays();
    const date = existingOffer.ExpirationDate();
    if (expirationType === DiscountExpirationType.Days && days !== undefined) {
      return {
        timeframeType: 'within',
        daysCount: days,
        selectedDate: addDays(now, days)
      };
    } else if (expirationType === DiscountExpirationType.Date && date) {
      return {
        timeframeType: 'until',
        daysCount: 30,
        selectedDate: fromDateStampObject(date)
      };
    }
  }
  const defaultDays = 30;
  return {
    timeframeType: 'within',
    daysCount: defaultDays,
    selectedDate: addDays(now, defaultDays)
  };
}
export function getUserForRole(newUserId) {
  return newUserId ? makeAssignedUserAssigned(newUserId) : makeAssignedUserUnassigned();
}